/*
	OneEduKit 1.0.0 Usage (Webpack)
	
	Education Kit Management
	Automatically handles removal of education kits when education products are removed
*/

// Import all dependencies here
import Backbone from 'backbone';

// Define a new console, to enable debugging logs please set 1r_debugging cookie with value of true using `ORW.utilities.setCookie('1r_debugging',true)`
const console = (function(oldCons){
	ORW.debugging = ORW.utilities.getCookie('1r_debugging');
    return {
        log: function(text){
            ORW.debugging == `true` && oldCons.log(text);
        },
        info: function (text) {
            ORW.debugging == `true` && oldCons.info(text);
        },
        warn: function (text) {
            ORW.debugging == `true` && oldCons.warn(text);
        },
        debug: oldCons.debug,
        error: function (text) {
            oldCons.error(text);
            if (window?.newrelic) {
                var err = new Error(text);
                window.newrelic.noticeError(err);
            }
        }
    };
}(window.console));

// Define the Backbone View here
const OneEduKitView = Backbone.View.extend({
	
	initialize: async function (settings) {
		const self = this

		console.log(`EduKit: Init Education Kit Manager ...`);

		// Action proxy to prevent concurrent operations
		self.action = {}
		self.actionProxy = new Proxy(self.action, {
			set: function (target, key, value) {
				console.log(`EduKit Action: ${key} - ${value}`)
				target[key] = value
				return true
			}
		})
		self.actionProxy.loading = false

		self.validateEducationKit()
		
		// Expose globally for module-oneCart to call
		window.ORW = window.ORW || {}
		window.ORW.eduKit = self
	},

	/**
	 * Remove orphaned kits when education product is missing
	 * Called from validateEducationKits function
	 * @param {Array} orphanedKitItems Array of cart items to remove, requires key property
	 */
	removeOrphanedEducationKits: async function(orphanedKitItems) {
		const self = this

		if (self.actionProxy && self.actionProxy.loading) {
			console.log('EduKit Removal: Skipped (EduKit action in progress)');
			return;
		}
		self.actionProxy.loading = true

		try {
			const updates = {}

			orphanedKitItems.forEach(item => {
				updates[item.key] = 0
			})
			console.log(`EduKit: Removal of orphaned kits:`, updates)

			// Single batched API call to remove both
			const url = window.Shopify.routes.root + 'cart/update.js'
			const config = {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json'
				},
				body: JSON.stringify({updates}),
			};

			const response = await fetch(url, config)
			if(!response.ok){
				throw new Error("Cart Update returned status code "+response.status);
			}

			console.log(`EduKit: Successfully removed orphaned kits`)
			self.eduKitChanged(response, `edu_kit_removed`)
		} catch (error) {
			console.error(`EduKit: Batched removal error`);
			console.error(error);
		} finally {
			self.actionProxy.loading = false
		}
	},

	/**
	 * Validate cart, remove orphan kits
	 * Called on initialization
	 */
	validateEducationKit: async function() {
		const self = this

		try{
			const cart = await this.fetchCart()
			const orphanedKits = []

			// find all education kits and mark for deletion if no matching class is found.
			cart.items.filter(item => {
				return item.properties.hasOwnProperty('_EDU_KIT')
			})
			.forEach(item => {
				const baseKit = cart.items.find(kit => {
					return item.properties._education_link_id && (kit.properties._education_link_id == item.properties._education_link_id) && !(kit.properties.hasOwnProperty('_EDU_KIT'))
				})
				if(!baseKit){
					orphanedKits.push(item);
				}
			});
			if(orphanedKits.length){
				self.removeOrphanedEducationKits(orphanedKits);
			}
		} catch (error) {
			console.error(`EduKit: Validation of kit error`);
			console.error(error);
		}
	},

	/**
	 * Remove education product and its linked kit together (batched)
	 * Called directly from module-oneCart when user clicks remove
	 * @param {String} productKey The cart key of the education product
	 * @param {String} educationLinkId The education link ID
	 */
	removeEducationProductWithKit: async function(productKey, educationLinkId) {
		const self = this

		if (self.actionProxy && self.actionProxy.loading) {
			console.log('EduKit: Skipped (EduKit action in progress)');
			return;
		}

		self.actionProxy.loading = true

		try {
			// Fetch current cart to find the kit
			const cart = await this.fetchCart()
			const updates = {}
			
			// Add education product to removal batch
			updates[productKey] = 0
			
			// Find linked kit and add to batch
			const kit = cart.items.find(item => 
				item.properties && 
				item.properties._education_link_id === educationLinkId &&
				item.properties._EDU_KIT === 'true'
			)
			
			if (kit) {
				console.log(`EduKit: Found linked kit to remove with product`)
				updates[kit.key] = 0
			}
			
			console.log(`EduKit: Batched removal:`, updates)
			
			// Single batched API call to remove both
			const url = window.Shopify.routes.root + 'cart/update.js'
			const config = {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json'
				},
				body: JSON.stringify({updates}),
			};
			
			const response = await fetch(url, config)
			if(!response.ok){
				throw new Error("Cart Update returned status code "+response.status);
			}
			
			console.log(`EduKit: Successfully removed education product + kit in single batch`)
			self.eduKitChanged(response, `edu_kit_removed`)
			
		} catch (error) {
			console.error(`EduKit: Batched removal error`);
			console.error(error);
		} finally {
			self.actionProxy.loading = false
		}
	},

	async fetchCart() {
		const response = await fetch('/cart.js');
		return response.json();
	},

	/**
	 * Update education product and kit quantity together (synced)
	 * Called from module-oneCart when quantity changes
	 * @param {String} productKey The cart key of the education product
	 * @param {String} educationLinkId The education link ID
	 * @param {Number} newQuantity The new quantity
	 */
	updateEducationProductWithKit: async function(productKey, educationLinkId, newQuantity) {
		const self = this

		if (self.actionProxy && self.actionProxy.loading) {
			console.log('EduKit: Skipped (EduKit action in progress)');
			return;
		}

		self.actionProxy.loading = true

		try {
			// Fetch current cart to find the kit
			const cart = await this.fetchCart()
			
			// Find linked kit
			const kit = cart.items.find(item => 
				item.properties && 
				item.properties._education_link_id === educationLinkId &&
				item.properties._EDU_KIT === 'true'
			)
			
			if (!kit) {
				console.log(`EduKit: No linked kit found for syncing`);
				self.actionProxy.loading = false;
				return;
			}
			
			console.log(`EduKit: Syncing - Product: ${productKey}, Kit: ${kit.key}, Quantity: ${newQuantity}`)
			
			// Update education product quantity first
			await fetch(window.Shopify.routes.root + 'cart/change.js', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ id: productKey, quantity: parseInt(newQuantity) })
			})
			
			// Then update kit quantity
			await fetch(window.Shopify.routes.root + 'cart/change.js', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ id: kit.key, quantity: parseInt(newQuantity) })
			})
			
			console.log(`EduKit: Successfully synced quantities`)
			self.eduKitChanged({}, `edu_kit_quantity_synced`)
			
		} catch (error) {
			console.error(`EduKit: Quantity sync error`);
			console.error(error);
		} finally {
			self.actionProxy.loading = false
		}
	},

	/**
	 * Dispatch custom event when education kit changes
	 * @param {Object} data Response data
	 * @param {String} eventNameOverride Custom event name
	 */
	eduKitChanged: function (data = {}, eventNameOverride = '') {
		const eventName = eventNameOverride ? eventNameOverride : `edu_kit_changed`
		const event = new CustomEvent(eventName, { detail: data });
		window.dispatchEvent(event);
	},
	
	destroy: function() {
		const self = this
		self.undelegateEvents()
	}
});

// Define the module here 
const OneEduKit = {
	init: function (settings) {
		return new OneEduKitView({
			el: document.body,
		});
	}
}

// Output the module
export default OneEduKit;
